import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { assistantBulletsContent, assistantContent } from '../../helpers/assistant.content';
import { Experiences } from '@local/client-contracts';
import { UntilDestroy } from '@ngneat/until-destroy';
import { cloneDeep, isEqual } from 'lodash';
import { AssistantsConstants } from '@local/common-web';
import { clampMax } from '@local/ts-infra';

type AssistantBullet = {
  feedbackReasoning: boolean;
  enableRelatedArticles: boolean;
};

@UntilDestroy()
@Component({
  selector: 'teams-preferences',
  templateUrl: './teams-preferences.component.html',
  styleUrls: ['../../base-preferences.scss', './teams-preferences.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsPreferencesComponent {
  readonly LIMIT_RELATED_ARTICLES_MAX = AssistantsConstants.RELATE_ARTICLES_DEFAULT_LIMIT;
  readonly LIMIT_RELATED_ARTICLES_MIN = 1;
  readonly assistantContent = assistantContent;
  readonly bulletsContent = assistantBulletsContent.teams;
  private assistantId: string;
  viewOnly: boolean;
  teamsProperties: Experiences.ExperienceTeamsSettings;
  knowledgeResourcesLimit: number;
  assistantBullets: AssistantBullet = {
    feedbackReasoning: true,
    enableRelatedArticles: true,
  };

  @Input() set assistant(val: Experiences.ExperienceItem<'teams'>) {
    const assistantChanged = !this.assistantId || val?.id !== this.assistantId;
    if (assistantChanged || !isEqual(val?.settings, this.teamsProperties)) {
      this.assistantId = val.id;
      this.viewOnly = val.permissionRole === 'viewer';
      this.teamsProperties = cloneDeep(val.settings);
      this.initBullets();
    }
  }
  @Output() updateProperties = new EventEmitter<Experiences.ExperienceTeamsSettings>();

  constructor(private cdr: ChangeDetectorRef) {}

  private initBullets() {
    this.assistantBullets = {
      feedbackReasoning: !!this.teamsProperties?.feedbackReasoning,
      enableRelatedArticles: !!this.teamsProperties?.knowledgeResourcesLimit,
    };
    this.initRelatedArticles();
    this.cdr.markForCheck();
  }

  private initRelatedArticles() {
    this.knowledgeResourcesLimit = this.teamsProperties.knowledgeResourcesLimit || this.LIMIT_RELATED_ARTICLES_MAX;
  }

  updateExperiencesBullets() {
    this.teamsProperties = {
      ...this.teamsProperties,
      feedbackReasoning: this.assistantBullets.feedbackReasoning,
      knowledgeResourcesLimit: !!this.assistantBullets.enableRelatedArticles ? this.knowledgeResourcesLimit : 0,
    };
    this.updateProperties.emit(this.teamsProperties);
  }

  onUpdateSync($event, id: string) {
    this.assistantBullets[id] = $event.checked;
    this.updateExperiencesBullets();
  }

  changeRelatedArticlesLimit($event: string) {
    const limitCount = Number($event);
    if (isNaN(limitCount)) {
      return;
    }
    this.knowledgeResourcesLimit = clampMax(this.LIMIT_RELATED_ARTICLES_MIN, limitCount, this.LIMIT_RELATED_ARTICLES_MAX);
    this.updateExperiencesBullets();
  }
}
